import React, { useEffect, useState } from "react";
import { ClipboardCheck, Plus, ScanLine } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge } from "@/components/common";
import BarcodeScanner from "@/components/BarcodeScanner";

const STATUS = { draft: ["Draft", "bg-slate-100 text-slate-600"], submitted: ["Menunggu Approval", "bg-amber-100 text-amber-700"], approved: ["Disetujui", "bg-emerald-100 text-emerald-700"], rejected: ["Ditolak", "bg-rose-100 text-rose-700"] };
const CATS = [["", "Semua Bahan"], ["fresh", "Bunga Segar"], ["artificial", "Artificial"], ["packaging", "Packaging"], ["finished", "Produk Jadi"]];

export default function Opname() {
  const { branch, can, isOwner } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const canApprove = can("stock_opname", "approve") || isOwner;

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/opname", branch ? { branch_id: branch.id } : {})); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  return (
    <>
      <PageHeader title="Stok Opname" subtitle={branch?.name} icon={ClipboardCheck}
        actions={can("stock_opname", "create") && <Button onClick={() => setCreating(true)} data-testid="add-opname-btn"><Plus size={16} /> Opname Baru</Button>} />

      {loading ? <Loading /> : !list.length ? <EmptyState icon={ClipboardCheck} title="Belum ada opname" message="Mulai hitung stok fisik (blind count)." /> : (
        <div className="space-y-2">
          {list.map((o) => {
            const [label, cls] = STATUS[o.status] || ["-", ""];
            return (
              <Card key={o.id} className="p-4 flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:border-primary" onClick={() => setDetail(o.id)} data-testid={`opname-${o.id}`}>
                <div>
                  <div className="font-medium text-slate-800">{o.opname_no}</div>
                  <div className="text-xs text-slate-400">{o.lines?.length || 0} item · {o.created_by_name} · {fmtDateTime(o.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {o.summary && <span className="text-xs text-slate-500">{o.summary.match} pas · {o.summary.over} lebih · {o.summary.short} kurang</span>}
                  <Badge className={cls}>{label}</Badge>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && <OpnameCreate branch={branch} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); setDetail(id); }} />}
      {detail && <OpnameDetail id={detail} canApprove={canApprove} onClose={() => setDetail(null)} onChanged={load} />}
    </>
  );
}

function OpnameCreate({ branch, onClose, onSaved }) {
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!branch) return toast.error("Pilih cabang dulu");
    setBusy(true);
    try { const o = await api.post("/opname", { branch_id: branch.id, category: category || null }); toast.success("Opname dibuat"); onSaved(o.id); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Opname Baru" maxWidth="max-w-sm">
      <div className="space-y-3">
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Kategori</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="of-category">{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <p className="text-xs text-slate-400">Hitungan bersifat blind — stok sistem disembunyikan sampai Anda submit.</p>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="of-save">{busy ? "Membuat..." : "Mulai Opname"}</Button>
      </div>
    </Modal>
  );
}

function OpnameDetail({ id, canApprove, onClose, onChanged }) {
  const [o, setO] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const [hit, setHit] = useState(null);
  const load = async () => { const d = await api.get(`/opname/${id}`); setO(d); const c = {}; d.lines.forEach((l) => { if (l.qty_counted != null) c[l.item_id] = l.qty_counted; }); setCounts(c); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Scan SKU/barcode → tambah 1 ke hitungan fisik baris yang cocok (percepat opname)
  const scanIncrement = (value) => {
    const n = String(value).trim().toLowerCase();
    if (!n || !o) return;
    const line = o.lines.find((l) => (l.sku || "").toLowerCase() === n || (l.item_id || "").toLowerCase() === n);
    if (!line) { toast.error(`SKU "${value}" tidak ada di opname ini`); return; }
    setCounts((c) => ({ ...c, [line.item_id]: Number(c[line.item_id] ?? 0) + 1 }));
    setHit(line.item_id);
    toast.success(`${line.name} +1`);
    setTimeout(() => setHit(null), 1200);
  };

  const saveCounts = async (submit) => {
    setBusy(true);
    try {
      const payload = { counts: o.lines.map((l) => ({ item_id: l.item_id, qty_counted: Number(counts[l.item_id] ?? 0) })) };
      await api.post(`/opname/${id}/counts`, payload);
      if (submit) { await api.post(`/opname/${id}/submit`); toast.success("Opname disubmit"); }
      else toast.success("Hitungan disimpan");
      await load(); onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const decide = async (action) => {
    let notes = "";
    if (action === "reject") { notes = window.prompt("Alasan?") || ""; if (!notes) return; }
    setBusy(true);
    try { await api.post(`/opname/${id}/${action}`, { notes }); toast.success(action === "approve" ? "Disetujui — stok disesuaikan" : "Ditolak"); await load(); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const isDraft = o?.status === "draft";
  const showVariance = o && o.status !== "draft" && o.lines[0]?.qty_system !== undefined;

  return (
    <Modal open onClose={onClose} title={o?.opname_no || "Opname"} maxWidth="max-w-2xl">
      {!o ? <Loading /> : (
        <div className="space-y-4">
          <Badge className={STATUS[o.status]?.[1]}>{STATUS[o.status]?.[0]}</Badge>
          {isDraft && (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") { scanIncrement(e.target.value); e.target.value = ""; } }}
                placeholder="Scan / ketik SKU lalu Enter → +1 fisik"
                data-testid="opname-scan-input"
                className="flex-1 h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-primary"
              />
              <button onClick={() => setScan(true)} data-testid="opname-scan-btn"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-primary/10 text-primary text-sm font-medium hover:bg-primary hover:text-white transition">
                <ScanLine size={16} /> Kamera
              </button>
            </div>
          )}
          <div className="border border-slate-100 rounded-xl overflow-hidden max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 px-3 font-medium">Item</th>
                {showVariance && <th className="py-2 px-3 font-medium text-right">Sistem</th>}
                <th className="py-2 px-3 font-medium text-right">Fisik</th>
                {showVariance && <th className="py-2 px-3 font-medium text-right">Selisih</th>}
              </tr></thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.item_id} className={`border-b border-slate-50 ${hit === l.item_id ? "bg-emerald-50" : ""}`}>
                    <td className="py-2 px-3 text-slate-700">{l.name} <span className="text-xs text-slate-400">{l.unit}{l.sku ? ` · ${l.sku}` : ""}</span></td>
                    {showVariance && <td className="py-2 px-3 text-right text-slate-500">{num(l.qty_system)}</td>}
                    <td className="py-2 px-3 text-right">
                      {isDraft ? (
                        <input type="number" value={counts[l.item_id] ?? ""} onChange={(e) => setCounts((c) => ({ ...c, [l.item_id]: e.target.value }))} className="w-20 h-8 px-2 rounded-lg border border-slate-300 text-right" />
                      ) : num(l.qty_counted)}
                    </td>
                    {showVariance && <td className={`py-2 px-3 text-right font-medium ${l.variance > 0 ? "text-emerald-600" : l.variance < 0 ? "text-rose-600" : "text-slate-400"}`}>{l.variance > 0 ? "+" : ""}{num(l.variance)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isDraft && (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => saveCounts(false)} disabled={busy}>Simpan</Button>
              <Button className="flex-1" onClick={() => saveCounts(true)} disabled={busy} data-testid="of-submit">Submit untuk Approval</Button>
            </div>
          )}
          {o.status === "submitted" && canApprove && (
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => decide("approve")} disabled={busy} data-testid="of-approve">Setujui & Sesuaikan Stok</Button>
              <Button variant="danger" className="flex-1" onClick={() => decide("reject")} disabled={busy}>Tolak</Button>
            </div>
          )}
        </div>
      )}
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onScanned={scanIncrement} title="Scan Item Opname" />
    </Modal>
  );
}
